import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  BanknotesIcon, 
  ArrowTrendingUpIcon 
} from "@heroicons/react/24/solid";

// Game card component with improved UI
const GameCard = ({ title, image, route, isComingSoon }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e) => {
    if (isComingSoon) {
      e.preventDefault();
    }
  };

  return (
    <Link 
      to={route} 
      onClick={handleClick}
      className="block rounded-lg overflow-hidden transition-all duration-300 hover:transform hover:scale-102 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {/* Game image with hover effect */}
        <div className="aspect-w-16 aspect-h-9 overflow-hidden">
          <img 
            src={image || "/api/placeholder/400/250"} 
            alt={title} 
            className={`w-full h-full object-cover transition-transform duration-500 ${
              isHovered ? "scale-110" : ""
            }`}
          />
        </div>
        
        {/* Gradient overlay on hover */}
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black via-transparent opacity-70 transition-opacity duration-300`} 
        />
        
        {/* Status badge */}
        <div className="absolute top-2 right-2">
          {isComingSoon ? (
            <div className="py-1 px-3 rounded-full bg-black bg-opacity-70 border border-yellow-500/50 text-yellow-400 text-xs font-medium">
              Coming Soon
            </div>
          ) : (
            <div className="py-1 px-3 rounded-full bg-black bg-opacity-70 border border-pink-500/50 text-pink-400 text-xs font-medium">
              Live
            </div>
          )}
        </div>
        
        {/* Game title */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-lg font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-violet-400 group-hover:to-pink-400 transition-all duration-300">
            {title}
          </h3>
        </div>
      </div>
    </Link>
  );
};

// Stats card with improved design
const StatCard = ({ label, value, icon: Icon, color }) => {
  return (
    <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg border border-gray-800 p-4 hover:border-gray-700 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-400">{label}</div>
        <Icon className="h-5 w-5 text-pink-400" />
      </div>
      <div className={`text-2xl font-bold mt-2 text-transparent bg-clip-text bg-gradient-to-r ${color}`}>
        {value}
      </div>
    </div>
  );
};

export default function Casino() {
  const games = [
    {
      id: 1,
      title: "Scatter Slots",
      image: "/scatter.jpg",
      route: "/casino/scatter-slots",
      isComingSoon: false
    },
    {
      id: 2,
      title: "Lottery",
      image: "/lottery.png",
      route: "/casino/lottery",
      isComingSoon: false
    },
    {
      id: 3,
      title: "Plinko",
      image: "/plinko.jpg",
      route: "/casino/plinko",
      isComingSoon: false
    },
    {
      id: 4,
      title: "Texas HOLD'EM",
      image: "/texas.png",
      route: "/casino/texas-holdem",
      isComingSoon: false
    }
  ];

  const stats = [
    {
      label: "Games Available",
      value: "4",
      icon: BanknotesIcon,
      color: "from-pink-500 to-red-500",
      id: "gamesAvailable"
    },
    {
      label: "Jackpot Pool",
      value: "250,000",
      icon: ArrowTrendingUpIcon,
      color: "from-violet-500 to-pink-500",
      id: "jackpotPool"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono -mt-16">
      {/* Header section with improved spacing */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-2">
          Casino Games
        </h1>
        <p className="text-sm text-gray-400 text-center max-w-2xl mx-auto mb-8">
          Experience premium casino games with high-quality graphics and massive rewards
        </p>
        
        {/* Stats section */}
        <div className="bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-xl p-4 md:p-6 mb-12">
          <div className="grid grid-cols-2 gap-6 md:gap-0 md:flex md:justify-around">
            {stats.map(stat => (
              <div key={stat.id} className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <stat.icon className="h-5 w-5 text-pink-400 mr-2" />
                  <span className="text-sm text-gray-400">{stat.label}</span>
                </div>
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-12 w-px bg-gradient-to-b from-violet-500/30 via-pink-500/30 to-red-500/30"></div>
                <div className={`text-lg md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${stat.color}`}>
                  {stat.label === "Jackpot Pool" ? `${stat.value} CHIPS` : stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Games grid with improved responsiveness */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
      <div className="flex items-center mb-6">
      <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
        Featured Games
      </h3>
      <div className="flex-grow h-px ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
      <div className="ml-2 px-2 py-1 bg-black bg-opacity-40 rounded text-xs text-pink-400 border border-pink-500/30">
        View All
      </div>
    </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {games.map((game) => (
            <GameCard
              key={game.id}
              title={game.title}
              image={game.image}
              route={game.route}
              isComingSoon={game.isComingSoon}
            />
          ))}
        </div>
      </div>

      {/* CTA section */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="px-6 py-4 text-center">


              <button 
                onClick={() => window.history.back()} 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-violet-600 to-pink-600 text-white font-medium hover:from-violet-700 hover:to-pink-700 transition-all duration-300 shadow-lg shadow-pink-600/20"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Arena</span>
              </button>
          </div>
        </div>
      </div>
    </div>
  );
}
